import { useEffect, useState } from "react";
import { Download, FileBarChart2, FileSpreadsheet } from "lucide-react";
import { API, api, money } from "@/util";

const PERIODS = [
  { key: "daily", label: "Günlük" },
  { key: "monthly", label: "Aylık" },
  { key: "yearly", label: "Yıllık" },
];

export default function Reports() {
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState({ rows: [], totals: { income: 0, expense: 0, net: 0 } });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .get("/reports", { params: { period } })
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], totals: { income: 0, expense: 0, net: 0 } }))
      .finally(() => setLoading(false));
  }, [period]);

  const download = async (format) => {
    setExporting(format);
    try {
      const response = await api.get("/reports/export", {
        params: { period, format },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.download = `voyage360-rapor-${period}-${stamp}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Rapor indirilemedi. Lütfen tekrar deneyin.");
    } finally {
      setExporting(null);
    }
  };

  return (
    <section className="page-section">
      <div className="section-header">
        <div>
          <span className="eyebrow">FİNANSAL RAPORLAR</span>
          <h2>Gelir-gider karşılaştırması</h2>
          <p>Nakit akışını günlük, aylık veya yıllık bazda özetle; eğilimleri gör.</p>
        </div>
        <div className="button-group">
          <button className="outline-button" onClick={() => download("csv")} disabled={exporting === "csv"} data-testid="export-csv-button">
            <FileSpreadsheet size={16} /> {exporting === "csv" ? "Hazırlanıyor…" : "CSV"}
          </button>
          <button className="primary-button" onClick={() => download("pdf")} disabled={exporting === "pdf"} data-testid="export-pdf-button">
            <Download size={16} /> {exporting === "pdf" ? "Hazırlanıyor…" : "PDF indir"}
          </button>
        </div>
      </div>

      <div className="tabs-row">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            className={`tab ${period === p.key ? "active" : ""}`}
            data-testid={`report-period-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="finance-summary">
        <div className="finance-chip positive" data-testid="report-total-income">
          <small>Toplam Gelir</small><b>{money(data.totals.income)}</b>
        </div>
        <div className="finance-chip negative" data-testid="report-total-expense">
          <small>Toplam Gider</small><b>{money(data.totals.expense)}</b>
        </div>
        <div className={`finance-chip ${data.totals.net >= 0 ? "positive" : "negative"}`} data-testid="report-net">
          <small>Net</small><b>{money(data.totals.net)}</b>
        </div>
      </div>

      {loading ? (
        <div className="empty-page">
          <p>Rapor hesaplanıyor…</p>
        </div>
      ) : data.rows.length ? (
        <div className="table-wrap">
          <table className="data-table" data-testid="reports-table">
            <thead>
              <tr>
                <th>Dönem</th>
                <th>Kayıt</th>
                <th className="right">Gelir</th>
                <th className="right">Gider</th>
                <th className="right">Net</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, idx) => (
                <tr key={row.period} data-testid={`report-row-${idx}`}>
                  <td><b>{row.period}</b></td>
                  <td className="muted">{row.count}</td>
                  <td className="right positive">{money(row.income)}</td>
                  <td className="right negative">{money(row.expense)}</td>
                  <td className={`right ${row.net >= 0 ? "positive" : "negative"}`}>
                    <b>{money(row.net)}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-page">
          <div className="empty-icon"><FileBarChart2 size={22} /></div>
          <h2>Henüz hareket yok</h2>
          <p>Finans Merkezi'nden ilk gelir/gider kaydını eklediğinde burada otomatik görünür.</p>
        </div>
      )}
    </section>
  );
}
